import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  makeStyles,
  TextField,
  Typography,
  Collapse,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AutorenewIcon from "@material-ui/icons/Autorenew";

import Dialog from "src/components/Dialog";
import ConfirmDialog from "src/components/ConfirmDialog";
import ColorPalettePill from "src/components/ColorPalettePill";
import ImageInput, { ClearSymbol } from "src/components/ImageInput";
import {
  CustomPalette,
  defaultLightPalette,
  defaultDarkPalette,
} from "src/theme";
import { useCustomPalette } from "src/contexts/ThemeContext";
import { useSelector } from "react-redux";
import { userCompanySelector } from "src/store/system/selector";
import { updateCompanyInformation } from "src/apiService/modules/system";
import { getUploadedFile } from "src/utils/storage";
import { toast } from "react-toastify";
import { generateTheme } from "src/apiService/modules/theme";
import { useRecordEvent } from "src/hooks/useRecordEvent";

const useStyles = makeStyles((theme) => ({
  "@keyframes smokeSwirl": {
    "0%": {
      transform: "translate(0, 0) rotate(0deg) scale(1)",
      opacity: 0.6,
    },
    "25%": {
      transform: "translate(15%, -20%) rotate(90deg) scale(1.2)",
      opacity: 0.9,
    },
    "50%": {
      transform: "translate(-10%, -40%) rotate(180deg) scale(0.9)",
      opacity: 0.7,
    },
    "75%": {
      transform: "translate(-20%, -15%) rotate(270deg) scale(1.1)",
      opacity: 0.8,
    },
    "100%": {
      transform: "translate(0, 0) rotate(360deg) scale(1)",
      opacity: 0.6,
    },
  },
  "@keyframes smokeRise": {
    "0%": {
      transform: "translateY(20%) scale(0.8)",
      opacity: 0,
    },
    "20%": {
      opacity: 0.8,
    },
    "80%": {
      opacity: 0.6,
    },
    "100%": {
      transform: "translateY(-30%) scale(1.3)",
      opacity: 0,
    },
  },
  "@keyframes smokePulse": {
    "0%, 100%": {
      filter: "blur(30px)",
    },
    "50%": {
      filter: "blur(50px)",
    },
  },
  "@keyframes colorFieldFadeIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    marginRight: -theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  generateSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    marginBottom: theme.spacing(3),
  },
  generateHeader: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  generateTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
  },
  generateSubtitle: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
  },
  generateContent: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  logoUploadWrapper: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    minHeight: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  uploadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    gap: theme.spacing(2),
    zIndex: 1,
  },
  uploadingText: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: "center",
  },
  generationOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(180deg, #0a1a1f 0%, #122630 50%, #184461 100%)",
    zIndex: 10,
  },
  smokeContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
    borderRadius: theme.shape.borderRadius,
  },
  smokeOrb: {
    position: "absolute",
    borderRadius: "50%",
    animation:
      "$smokeSwirl 12s ease-in-out infinite, $smokePulse 4s ease-in-out infinite",
  },
  smokeRising: {
    position: "absolute",
    borderRadius: "50%",
    animation:
      "$smokeRise 6s ease-out infinite, $smokePulse 3s ease-in-out infinite",
  },
  generationTextContainer: {
    position: "relative",
    zIndex: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  generationText: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
    textAlign: "center",
    textShadow:
      "0 2px 15px rgba(68, 112, 147, 0.6), 0 0 30px rgba(28, 93, 59, 0.4)",
  },
  editorWrapper: {
    position: "relative",
  },
  editorContent: {
    transition: "opacity 1s ease-in-out",
  },
  editorHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
  },
  previewCard: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  previewLabel: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: theme.spacing(1),
  },
  textPreview: {
    marginBottom: theme.spacing(1.5),
  },
  textPreviewHeading: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(0.5),
  },
  textPreviewBody: {
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1.4,
  },
  editorGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(3),
  },
  editorColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  colorFieldAnimated: {
    animation: "$colorFieldFadeIn 0.4s ease-out forwards",
    opacity: 0,
  },
  colorFieldHidden: {
    opacity: 0,
  },
  columnHeader: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  colorField: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  colorFieldLabel: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
  },
  colorInputWrapper: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  colorPicker: {
    width: 50,
    height: 44,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    overflow: "hidden",
    padding: 0,
    "& input[type='color']": {
      width: "100%",
      height: "100%",
      border: "none",
      cursor: "pointer",
      padding: 0,
      margin: 0,
    },
  },
  colorTextInput: {
    flex: 1,
    "& input": {
      fontFamily: "'Courier New', monospace",
      fontSize: theme.typography.body2.fontSize,
    },
  },
  footerLeft: {
    display: "flex",
    gap: theme.spacing(1),
  },
  logoLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
  },
  pillWrapper: {
    marginTop: theme.spacing(3),
  },
}));

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

function ColorField({ label, value, onChange }: ColorFieldProps) {
  const classes = useStyles();
  const [textValue, setTextValue] = useState(value);

  useEffect(() => {
    setTextValue(value);
  }, [value]);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    onChange(newValue);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTextValue(newValue);
    // Only update if it's a valid hex color
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue);
    }
  };

  // Convert rgb to hex for color picker
  const hexValue = value.startsWith("#") ? value : rgbToHex(value);

  return (
    <div className={classes.colorField}>
      <label className={classes.colorFieldLabel}>{label}</label>
      <div className={classes.colorInputWrapper}>
        <div className={classes.colorPicker}>
          <input type="color" value={hexValue} onChange={handleColorChange} />
        </div>
        <TextField
          className={classes.colorTextInput}
          variant="outlined"
          size="small"
          value={textValue}
          onChange={handleTextChange}
        />
      </div>
    </div>
  );
}

// Helper to convert rgb string to hex
function rgbToHex(rgb: string): string {
  if (rgb.startsWith("#")) return rgb;

  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return "#000000";

  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

interface ContentProps {
  onClose: () => void;
}

function Content({ onClose }: ContentProps) {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const {
    customLightPalette,
    customDarkPalette,
    setCustomLightPalette,
    setCustomDarkPalette,
  } = useCustomPalette();

  const company = useSelector(userCompanySelector);
  const [generateExpanded, setGenerateExpanded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerationAnimation, setShowGenerationAnimation] = useState(false);
  const [generationPhase, setGenerationPhase] = useState<"cloud" | "reveal">(
    "cloud"
  );
  const [showColorFieldAnimation, setShowColorFieldAnimation] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [uploadMessageIndex, setUploadMessageIndex] = useState(0);
  const uploadMessages = [
    "Personalizing your Genie now...",
    "Uploading Logo...",
  ];
  const uploadIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const editorWrapperRef = useRef<HTMLDivElement | null>(null);

  // Alternate upload messages every 3 seconds
  useEffect(() => {
    if (isUploadingLogo) {
      setUploadMessageIndex(0);
      uploadIntervalRef.current = setInterval(() => {
        setUploadMessageIndex((prev) => (prev + 1) % uploadMessages.length);
      }, 3000);
    } else {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
        uploadIntervalRef.current = null;
      }
    }

    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }
    };
  }, [isUploadingLogo, uploadMessages.length]);

  // Load the logo URL from storage on mount only
  // (uploads set logoUrl directly via blob URL to avoid race conditions)
  useEffect(() => {
    if (company?.logo) {
      getUploadedFile(company.logo)
        .then((url) => setLogoUrl(url))
        .catch(() => setLogoUrl(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoChange = async (file: File | typeof ClearSymbol | null) => {
    if (!file || file === ClearSymbol) {
      // Clear immediately for responsive UI
      setLogoUrl(null);
      setIsUploadingLogo(true);
      try {
        await updateCompanyInformation({ logo: undefined });
        recordEvent({
          type: "feature-adoption",
          feature: "logo-upload",
          element: "edit-colors-dialog",
          action: "remove",
        });
      } catch (e) {
        toast.error("Failed to remove logo");
      } finally {
        setIsUploadingLogo(false);
      }
      return;
    }

    setIsUploadingLogo(true);
    try {
      await updateCompanyInformation({ logo: file });
      // Set the local preview immediately from the uploaded file
      const localUrl = URL.createObjectURL(file);
      setLogoUrl(localUrl);
      recordEvent({
        type: "feature-adoption",
        feature: "logo-upload",
        element: "edit-colors-dialog",
        action: "upload",
      });
    } catch (e) {
      toast.error("Failed to upload logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const [lightPalette, setLightPalette] =
    useState<CustomPalette>(customLightPalette);
  const [darkPalette, setDarkPalette] =
    useState<CustomPalette>(customDarkPalette);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    const lightChanged = Object.keys(lightPalette).some(
      (key) =>
        lightPalette[key as keyof CustomPalette] !==
        customLightPalette[key as keyof CustomPalette]
    );
    const darkChanged = Object.keys(darkPalette).some(
      (key) =>
        darkPalette[key as keyof CustomPalette] !==
        customDarkPalette[key as keyof CustomPalette]
    );
    return lightChanged || darkChanged;
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowExitConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirm(false);
    onClose();
  };

  const handleLightChange = (key: keyof CustomPalette) => (value: string) => {
    setLightPalette((prev) => ({ ...prev, [key]: value }));
    recordEvent({
      type: "feature-adoption",
      feature: "palette-color-changed",
      element: key,
      mode: "light",
    });
  };

  const handleDarkChange = (key: keyof CustomPalette) => (value: string) => {
    setDarkPalette((prev) => ({ ...prev, [key]: value }));
    recordEvent({
      type: "feature-adoption",
      feature: "palette-color-changed",
      element: key,
      mode: "dark",
    });
  };

  const handleResetToDefaults = () => {
    setLightPalette({ ...defaultLightPalette });
    setDarkPalette({ ...defaultDarkPalette });
    recordEvent({
      type: "feature-adoption",
      feature: "palette-reset",
    });
  };

  const handleSave = () => {
    setCustomLightPalette(lightPalette);
    setCustomDarkPalette(darkPalette);
    recordEvent({
      type: "feature-adoption",
      feature: "palette-saved",
    });
    onClose();
  };

  const handleGeneratePalette = async () => {
    if (!company?.logo) return;

    setIsGenerating(true);
    setShowGenerationAnimation(true);
    setGenerationPhase("cloud");

    // Scroll to view the animation
    setTimeout(() => {
      editorWrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);

    const startTime = Date.now();
    const MIN_ANIMATION_TIME = 6000; // 6 seconds minimum

    try {
      const result = await generateTheme();
      const themes = {
        light: {
          bg: result.data.light.background,
          card: result.data.light.card,
          text: result.data.light.text,
          border: result.data.light.border,
          accent: result.data.brandLight.accent,
          brandPrimary: result.data.brandLight.primary,
          brandSecondary: result.data.brandLight.secondary,
        },
        dark: {
          bg: result.data.dark.background,
          card: result.data.dark.card,
          text: result.data.dark.text,
          border: result.data.dark.border,
          accent: result.data.brandDark.accent,
          brandPrimary: result.data.brandDark.primary,
          brandSecondary: result.data.brandDark.secondary,
        },
      };

      // Ensure minimum animation time
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_ANIMATION_TIME - elapsed);

      if (remainingTime > 0)
        await new Promise((resolve) => setTimeout(resolve, remainingTime));

      // Start reveal phase - cross-fade
      setGenerationPhase("reveal");

      // Update palettes
      setLightPalette(themes.light);
      setDarkPalette(themes.dark);

      // Record event for successful palette generation
      recordEvent({
        type: "feature-adoption",
        feature: "palette-generated",
      });

      // Wait for the main content to fade in first
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Then trigger staggered color field animation
      setShowColorFieldAnimation(true);

      // Wait for staggered animation to complete
      await new Promise((resolve) => setTimeout(resolve, 1200));

      setShowGenerationAnimation(false);
      setShowColorFieldAnimation(false);
    } catch (error) {
      console.error("Failed to generate palette:", error);
      toast.error(
        "Failed to generate color palette from logo. Please try again."
      );
      setShowGenerationAnimation(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const colorFields: { key: keyof CustomPalette; label: string }[] = [
    { key: "bg", label: "Background Color" },
    { key: "card", label: "Card Background" },
    { key: "text", label: "Text Color" },
    { key: "border", label: "Border Color" },
    { key: "accent", label: "Accent Color" },
    { key: "brandPrimary", label: "Brand Primary (Charts)" },
    { key: "brandSecondary", label: "Brand Secondary (Charts)" },
  ];

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Edit Custom Colors</Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className={classes.generateSection}>
          <div
            className={classes.generateHeader}
            onClick={() => setGenerateExpanded(!generateExpanded)}
          >
            <div>
              <div className={classes.generateTitle}>
                <span>Generate from Logo</span>
              </div>
              <div className={classes.generateSubtitle}>
                Upload your logo to automatically generate a color palette
              </div>
            </div>
            {generateExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>

          <Collapse in={generateExpanded}>
            <div className={classes.generateContent}>
              <Typography variant="body2" className={classes.logoLabel}>
                Company Logo
              </Typography>
              <div className={classes.logoUploadWrapper}>
                {isUploadingLogo && (
                  <div className={classes.uploadingOverlay}>
                    <CircularProgress size={48} color="primary" />
                    <Fade in key={uploadMessageIndex} timeout={500}>
                      <Typography className={classes.uploadingText}>
                        {uploadMessages[uploadMessageIndex]}
                      </Typography>
                    </Fade>
                  </div>
                )}
                <ImageInput
                  imageUrl={logoUrl}
                  onChange={handleLogoChange}
                  size="large"
                  placeholderText="Drag and drop your logo here or click to upload"
                />
              </div>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={!logoUrl || isGenerating || isUploadingLogo}
                onClick={handleGeneratePalette}
                startIcon={
                  isGenerating ? <AutorenewIcon className="rotating" /> : null
                }
              >
                {isUploadingLogo
                  ? "Uploading..."
                  : isGenerating
                  ? "Analyzing logo..."
                  : logoUrl
                  ? "Generate Palette"
                  : "Upload a logo to generate palette"}
              </Button>
            </div>
          </Collapse>
        </div>

        {/* Color Editor Wrapper with Generation Animation */}
        <div className={classes.editorWrapper} ref={editorWrapperRef}>
          {/* Magical Smoke Animation Overlay */}
          <Fade
            in={showGenerationAnimation && generationPhase === "cloud"}
            timeout={{ enter: 500, exit: 1000 }}
          >
            <div className={classes.generationOverlay}>
              <div className={classes.smokeContainer}>
                {/* Swirling smoke orbs - brand blue/green palette */}
                <div
                  className={classes.smokeOrb}
                  style={{
                    width: "70%",
                    height: "70%",
                    top: "30%",
                    left: "15%",
                    background:
                      "radial-gradient(ellipse at center, rgba(24, 68, 97, 0.7) 0%, transparent 70%)",
                    animationDelay: "0s",
                  }}
                />
                <div
                  className={classes.smokeOrb}
                  style={{
                    width: "50%",
                    height: "50%",
                    top: "20%",
                    right: "10%",
                    background:
                      "radial-gradient(ellipse at center, rgba(68, 112, 147, 0.6) 0%, transparent 70%)",
                    animationDelay: "-3s",
                  }}
                />
                <div
                  className={classes.smokeOrb}
                  style={{
                    width: "60%",
                    height: "60%",
                    bottom: "10%",
                    left: "20%",
                    background:
                      "radial-gradient(ellipse at center, rgba(4, 76, 38, 0.6) 0%, transparent 70%)",
                    animationDelay: "-6s",
                  }}
                />
                <div
                  className={classes.smokeOrb}
                  style={{
                    width: "45%",
                    height: "45%",
                    top: "40%",
                    right: "25%",
                    background:
                      "radial-gradient(ellipse at center, rgba(28, 93, 59, 0.5) 0%, transparent 70%)",
                    animationDelay: "-2s",
                  }}
                />

                {/* Rising smoke wisps */}
                <div
                  className={classes.smokeRising}
                  style={{
                    width: "30%",
                    height: "40%",
                    bottom: "0%",
                    left: "35%",
                    background:
                      "radial-gradient(ellipse at center, rgba(24, 68, 97, 0.5) 0%, transparent 70%)",
                    animationDelay: "0s",
                  }}
                />
                <div
                  className={classes.smokeRising}
                  style={{
                    width: "25%",
                    height: "35%",
                    bottom: "0%",
                    left: "55%",
                    background:
                      "radial-gradient(ellipse at center, rgba(4, 76, 38, 0.5) 0%, transparent 70%)",
                    animationDelay: "-2s",
                  }}
                />
                <div
                  className={classes.smokeRising}
                  style={{
                    width: "28%",
                    height: "38%",
                    bottom: "0%",
                    left: "20%",
                    background:
                      "radial-gradient(ellipse at center, rgba(68, 112, 147, 0.5) 0%, transparent 70%)",
                    animationDelay: "-4s",
                  }}
                />
              </div>

              <div className={classes.generationTextContainer}>
                <Fade
                  in={showGenerationAnimation}
                  timeout={4000}
                  style={{ transitionDelay: "1000ms" }}
                >
                  <Typography className={classes.generationText}>
                    Creating your personalized color theme...
                  </Typography>
                </Fade>
              </div>
            </div>
          </Fade>

          {/* Actual Content */}
          <div
            className={`${classes.editorContent} ${
              showGenerationAnimation && generationPhase === "cloud"
                ? classes.editorHidden
                : ""
            }`}
          >
            {/* Live Preview Section */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Live Preview
              </Typography>
              <div className={classes.previewGrid}>
                {/* Light Mode Preview */}
                <div
                  className={classes.previewCard}
                  style={{ backgroundColor: lightPalette.bg }}
                >
                  <Typography
                    className={classes.previewLabel}
                    style={{ color: lightPalette.text }}
                  >
                    Light Mode
                  </Typography>
                  <div className={classes.textPreview}>
                    <Typography
                      className={classes.textPreviewBody}
                      style={{ color: lightPalette.text }}
                    >
                      Body text appears in this color
                    </Typography>
                  </div>
                  <div className={classes.pillWrapper}>
                    <ColorPalettePill
                      palette={lightPalette}
                      size="small"
                      interactive={false}
                    />
                  </div>
                </div>

                {/* Dark Mode Preview */}
                <div
                  className={classes.previewCard}
                  style={{ backgroundColor: darkPalette.bg }}
                >
                  <Typography
                    className={classes.previewLabel}
                    style={{ color: darkPalette.text }}
                  >
                    Dark Mode
                  </Typography>
                  <div className={classes.textPreview}>
                    <Typography
                      className={classes.textPreviewBody}
                      style={{ color: darkPalette.text }}
                    >
                      Body text appears in this color
                    </Typography>
                  </div>
                  <div className={classes.pillWrapper}>
                    <ColorPalettePill
                      palette={darkPalette}
                      size="small"
                      interactive={false}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Color Editor Section */}
            <div className={classes.section}>
              <div className={classes.editorGrid}>
                {/* Light Mode Column */}
                <div className={classes.editorColumn}>
                  <Typography className={classes.columnHeader}>
                    Light Mode Colors
                  </Typography>
                  {colorFields.map(({ key, label }, index) => (
                    <div
                      key={`light-${key}`}
                      className={
                        showColorFieldAnimation
                          ? classes.colorFieldAnimated
                          : showGenerationAnimation
                          ? classes.colorFieldHidden
                          : undefined
                      }
                      style={
                        showColorFieldAnimation
                          ? { animationDelay: `${index * 100}ms` }
                          : undefined
                      }
                    >
                      <ColorField
                        label={label}
                        value={rgbToHex(lightPalette[key])}
                        onChange={handleLightChange(key)}
                      />
                    </div>
                  ))}
                </div>

                {/* Dark Mode Column */}
                <div className={classes.editorColumn}>
                  <Typography className={classes.columnHeader}>
                    Dark Mode Colors
                  </Typography>
                  {colorFields.map(({ key, label }, index) => (
                    <div
                      key={`dark-${key}`}
                      className={
                        showColorFieldAnimation
                          ? classes.colorFieldAnimated
                          : showGenerationAnimation
                          ? classes.colorFieldHidden
                          : undefined
                      }
                      style={
                        showColorFieldAnimation
                          ? { animationDelay: `${index * 100}ms` }
                          : undefined
                      }
                    >
                      <ColorField
                        label={label}
                        value={rgbToHex(darkPalette[key])}
                        onChange={handleDarkChange(key)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <div className={classes.footerLeft}>
          <Button onClick={handleResetToDefaults}>Reset to Defaults</Button>
        </div>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={showExitConfirm}
        title="Unsaved Changes"
        text="Are you sure you want to exit? Your custom color changes will be lost."
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={handleConfirmExit}
        cancel="Keep Editing"
        confirm="Exit Without Saving"
      />
    </>
  );
}

interface EditColorsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function EditColorsDialog({ open, onClose }: EditColorsDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Content onClose={onClose} />
    </Dialog>
  );
}

export default EditColorsDialog;
